import {User} from 'oidc-client';
import {store} from '../app/store';
import {getAuth} from '../app/utils/auth';
import {getConfig} from '../app/utils/config';
import Mime from '../app/utils/mime';

type Request = RequestInit & {
  handleUnauthorized?: boolean;
}

interface Client {
  <T = any>(endpoint: string, config?: Request): Promise<T>;

  get<T = any>(endpoint: string, config?: Request): Promise<T>;
  getFile<U = any>(endpoint: string, method: 'POST' | 'GET', body?: U, config?: Request): Promise<File>;
  delete<T = any>(endpoint: string, config?: Request): Promise<T>;
  post<T = any, U = any>(endpoint: string, body: U, config?: Request): Promise<T>;
  put<T = any, U = any>(endpoint: string, body: U, config?: Request): Promise<T>;
}

export class HttpError extends Error {
  private static getMessage(data: any): string {
    console.log('getMessage', data);
    if (typeof data === 'string') {
      return data;
    } else if (typeof data?.detail === 'string') {
      return data.detail;
    } else if (typeof data?.title === 'string') {
      return data.title;
    } else if (typeof data?.message === 'string') {
      return data.message;
    } else if (typeof data !== 'undefined') {
      return JSON.stringify(data);
    }
    return 'Noe gikk galt';
  }

  data: any;
  statusCode: number;
  traceId: string;

  constructor(data: any, res: Response) {
    super(HttpError.getMessage(data));
    this.statusCode = res.status;
    this.traceId = data?.referanseId;
    this.data = data;
  }
}

export function checkTrailingSlash(url: string): string{
  return url.match(/\/+$/g) ? url : url + '/';
}
export const client: Client = async (endpoint, fetchConfig = {}) => {
  const config = await getConfig();
  const auth = await getAuth(config);
  const konsulentToken = store.getState().consultantSettings.consultantToken;
  const origin = new URL(checkTrailingSlash(config.urls.api)).origin;

  if (fetchConfig.body) {
    fetchConfig.method = fetchConfig.method ?? 'POST';
    if (fetchConfig.body instanceof Blob) {
      fetchConfig.body = await fetchConfig.body.arrayBuffer();
    } else if (!(fetchConfig.body instanceof ArrayBuffer)) {
      fetchConfig.body = JSON.stringify(fetchConfig.body);
    }
    fetchConfig.headers = {
      'Content-Type': 'application/json',
      ...fetchConfig.headers,
    };
  }

  const url = new URL(endpoint, checkTrailingSlash(config.urls.api));
  const request = new Request(url.toString(), fetchConfig);

  if (url.origin === origin) {
    if (!request.headers.has('Authorization')) {
      request.headers.set('Authorization', `Bearer ${auth.user.access_token}`);
    }
    if (konsulentToken && !request.headers.has('X-Konsulent-Authorization')) {
      request.headers.set('X-Konsulent-Authorization', konsulentToken);
    }
  }

  let response = await window.fetch(request);
  if (fetchConfig.handleUnauthorized !== false && response.status === 401 && url.origin === origin) {
    try {
      let user: User;
      try {
        console.warn('401 response from API. Trying to silently renew token', response);
        user = await auth.client.signinSilent();
      } catch (err) {
        console.warn('Error in silent renew. Trying popup renew', err);
        user = await auth.client.signinPopup();
      }

      const newReq = request.clone();
      newReq.headers.set('Authorization', `Bearer ${user.access_token}`);
      const newRes = await fetch(newReq);
      if (newRes.ok) {
        response = newRes;
      }
    } catch (err) {
      console.warn('Unable to renew token. Will just respond with original 401 response', err);
    }
  }

  const contentType = new Mime(response.headers.get('Content-Type'));
  let data = null;
  if (contentType.hasSubtype('json')) {
    data = await response.json();
  } else if (contentType.isType('text')) {
    data = await response.text();
  } else if (request.headers.get('ResponseType') === 'blob') {
    data = await readFile(response);
  }

  if (response.ok) {
    return data;
  }
  if(!data) {
    switch(response.status) {
      case 403:
        data = 'Ingen tilgang';
        break;
    }
  }
  console.log('throw new HttpError', data, response);
  throw new HttpError(data, response);
};

client.get = (endpoint, customConfig = {}) =>
  client(endpoint, { ...customConfig, method: 'GET' });

client.getFile = (endpoint, method, body: any, customConfig = {}) => 
  client(endpoint, { ...customConfig, body, method, headers: {...customConfig.headers, ResponseType: 'blob'} });
 
client.post = (endpoint, body: any, customConfig = {}) =>
  client(endpoint, { ...customConfig, body, method: 'POST' });

client.put = (endpoint, body: any, customConfig = {}) =>
  client(endpoint, { ...customConfig, body, method: 'PUT' });

client.delete = (endpoint, customConfig = {}) =>
  client(endpoint, { ...customConfig, method: 'DELETE' });


async function readFile(response: Response): Promise<File | null> {
  const contentDisposition = response.headers.get('content-disposition');
  const fileNameMatch = contentDisposition ? /filename="?([^"]*?)"?(;|$)/g.exec(contentDisposition): undefined;
  const fileName = fileNameMatch && fileNameMatch.length > 1 ? fileNameMatch[1] : undefined;
  const blob = await response.blob();
  if(blob.size > 0) return new File([blob], fileName ?? 'fil');
  else return null;
}
